/* eslint-disable react/jsx-props-no-spreading */
import { ComponentProps, FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Svg } from '@altermaind/web/ui';
import {
  CheckboxesProps,
  Column,
  PaginatedTable,
  StyledTable,
  ValueInTable,
  usePaginatedTable,
} from '@/_shared/components';
import { Routes } from '@/_shared/constants/routes';
import { useDialog } from '@/_shared/hooks/useDialog';
import { getEmployeesCall } from '../../state';
import { PAGE_SIZE_EMPLOYEES } from '../../state/constants';
import { Employer } from '../../state/types';

type Props = {
  setSelectedEmployer: (employee: Employer | undefined) => void;
  selectedEmployer: Employer | undefined;
  paginedProps: ReturnType<typeof usePaginatedTable>;
  drawer: ComponentProps<typeof StyledTable>['drawer'];
  filters?: { q: string; departments?: string[]; roles?: string[] };
  showCheckboxes?: boolean;
};

export const TableEmployees: FC<Props> = ({
  setSelectedEmployer,
  selectedEmployer,
  paginedProps,
  drawer,
  filters,
  showCheckboxes = false,
}) => {
  const t = useTranslations('people.tableEmployees');
  const tStatus = useTranslations('people.status');
  const navigate = useNavigate();

  const {
    loading: loadingGetEmployees,
    error: errorGetEmployees,
    execute: executeGetEmployees,
    reset: resetGetEmployees,
  } = getEmployeesCall.hook();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { openDialog: openAssignDepartmentDialog } = useDialog<{
    employeeIds: string[];
    onSuccess: () => void;
  }>('assignDepartmentBulk');

  const goToDetail = (id: string) => {
    navigate(Routes.PeopleDetail.replace(':peopleId', id));
  };

  const columns: Column[] = [
    {
      key: 'firstName',
      title: t('firstName'),
      renderValue: (item: Employer) => (
        <ValueInTable
          inError={item.issues?.some(issue => issue.fieldName === 'firstName') ?? false}
        >
          {item.firstName || '-'}
        </ValueInTable>
      ),
    },
    {
      key: 'lastName',
      title: t('lastName'),
      renderValue: (item: Employer) => (
        <ValueInTable inError={item.issues?.some(issue => issue.fieldName === 'lastName') ?? false}>
          {item.lastName || '-'}
        </ValueInTable>
      ),
    },
    {
      key: 'email',
      title: t('email'),
      renderValue: (item: Employer) => (
        <ValueInTable inError={item.issues?.some(issue => issue.fieldName === 'email') ?? false}>
          {item.email || '-'}
        </ValueInTable>
      ),
    },
    {
      key: 'role',
      title: t('role'),
      renderValue: (item: Employer) => (
        <ValueInTable inError={item.issues?.some(issue => issue.fieldName === 'role') ?? false}>
          {item.role || '-'}
        </ValueInTable>
      ),
    },
    {
      key: 'department',
      title: t('department'),
      renderValue: (item: Employer) => (
        <ValueInTable
          inError={item.issues?.some(issue => issue.fieldName === 'department') ?? false}
        >
          {item.department?.name?.toLowerCase() === 'unknown' ? '-' : item.department?.name}
        </ValueInTable>
      ),
    },
    {
      key: 'status',
      title: t('status.label'),
      titleTooltip: t('status.tooltip'),
      renderValue: (item: Employer) => (
        <ValueInTable inError={item.issues?.some(issue => issue.fieldName === 'status') ?? false}>
          {item.status ? tStatus(`${item.status}`) : ''}
        </ValueInTable>
      ),
    },
    {
      key: 'edit',
      title: '',
      width: '60px',
      renderValue: (item: Employer) =>
        item.id === selectedEmployer?.id ? (
          <Box
            display='flex'
            flexDirection='row'
            alignItems='flex-end'
            justifyContent='flex-end'
            width='100%'
            style={{ cursor: 'pointer' }}
            onClick={() => goToDetail(item.id)}
          >
            <Svg name='arrowRight' size='s' />
          </Box>
        ) : null,
    },
  ];

  const checkboxesProps = {
    showCheckboxes,
    selectedItems: showCheckboxes ? selectedItems : undefined,
    onSelectionChange: showCheckboxes ? setSelectedItems : undefined,
    bulkActionLabel: showCheckboxes ? t('bulkActionAssignDepartment') : undefined,
    bulkActionOnClick: showCheckboxes
      ? () =>
          openAssignDepartmentDialog({
            employeeIds: selectedItems,
            onSuccess: () => setSelectedItems([]),
          })
      : undefined,
    getRowId: (item: Employer) => item.id,
  } as CheckboxesProps;

  useEffect(() => {
    if (!loadingGetEmployees && !errorGetEmployees) {
      executeGetEmployees({
        start: 0,
        length: PAGE_SIZE_EMPLOYEES,
        q: filters?.q || '',
        departments: filters?.departments,
        roles: filters?.roles,
      });
    }

    return () => {
      resetGetEmployees();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, executeGetEmployees]);

  useEffect(() => {
    if (loadingGetEmployees) {
      setSelectedEmployer(undefined);
    }
  }, [loadingGetEmployees, setSelectedEmployer]);

  return (
    <StyledTable
      hasElement={selectedEmployer !== undefined}
      error={!!errorGetEmployees}
      loading={loadingGetEmployees}
      drawer={drawer}
    >
      <PaginatedTable
        header-data-testid='table-employer-header'
        row-data-testid='table-employer-row'
        columns={columns}
        pageSize={PAGE_SIZE_EMPLOYEES}
        call={{ ...getEmployeesCall }}
        onRowClick={(employee: Employer) => {
          goToDetail(employee.id);
        }}
        onHoverRow={(employee: Employer) => {
          setSelectedEmployer(employee);
        }}
        rowWrapperProps={(employee: Employer) => {
          return employee.id === selectedEmployer?.id
            ? {
                backgroundColor: 'grey/dark/background',
                borderRadius: '10px',
                position: 'relative',
                cursor: 'pointer !important',
              }
            : {};
        }}
        paginedProps={paginedProps}
        loading={loadingGetEmployees}
        skeletonProps={{
          hasHeaderRow1: false,
          hasHeaderRow2: true,
          rows: 1,
        }}
        hasHover
        {...checkboxesProps}
      />
    </StyledTable>
  );
};
